import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useOpenReturn } from '../hooks/use-return-mutations';
import {
  emptyOpenReturnForm,
  openReturnSchema,
  toOpenReturnBody,
  type OpenReturnInput,
  type OpenReturnOutput,
} from '../schemas/open-return-schema';
import { returnableLines } from '../schemas/return-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'lines',
  'reason',
  'note',
  'resize',
  'staffNote',
  'returnShippingPaidBy',
]);

/**
 * The backend names lines by their index in the body, which skips unselected rows; map that
 * back to the form row so the message lands next to the right line.
 */
function lineFieldMapper(values: OpenReturnOutput) {
  const sent = values.lines
    .map((line, index) => ({ index, quantity: Number(line.quantity) }))
    .filter((line) => line.quantity > 0)
    .map((line) => line.index);
  return (field: string) => {
    const match = /^lines\[(\d+)\]\.(\w+)$/.exec(field);
    if (!match) return field;
    const row = sent[Number(match[1])];
    return row === undefined
      ? 'lines'
      : `lines.${row}.${match[2] === 'quantity' ? 'quantity' : 'orderLineId'}`;
  };
}

export interface OpenReturnDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Opens a return for the shopper. Only shipped units no other return holds can come back. */
export function OpenReturnDialog({ storeId, order, open, onOpenChange }: OpenReturnDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const navigate = useNavigate();
  const create = useOpenReturn(storeId);
  const lines = useMemo(() => returnableLines(order), [order]);
  const schema = useMemo(() => openReturnSchema(order), [order]);
  const form = useForm<OpenReturnInput, unknown, OpenReturnOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyOpenReturnForm(order),
  });
  const linesError =
    form.formState.errors.lines?.root?.message ?? form.formState.errors.lines?.message;

  const close = () => {
    onOpenChange(false);
    create.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const ret = await create.mutateAsync({ orderId: order.id, body: toOpenReturnBody(values) });
      close();
      void navigate({
        to: '/s/$storeId/returns/$returnId',
        params: { storeId, returnId: ret.id },
      });
    } catch (error) {
      const mapField = lineFieldMapper(values);
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) {
        if (error instanceof ApiError && typeof error.meta.remaining === 'number') {
          const field = error.details[0]?.field;
          const target = field ? mapField(field.replace(/^body\./, '')) : null;
          if (target?.endsWith('.quantity')) {
            form.setError(target as `lines.${number}.quantity`, {
              type: 'server',
              message: t('orders:returns.errors.remaining', { count: error.meta.remaining }),
            });
          }
        }
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:returns.open.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:returns.open.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">{t('orders:returns.open.items')}</legend>
              {linesError ? (
                <p className="text-destructive text-sm" role="alert">
                  {linesError}
                </p>
              ) : null}
              {lines.map(({ line, max }, index) => (
                <FormField
                  key={line.id}
                  control={form.control}
                  name={`lines.${index}.quantity`}
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-row items-start justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0 space-y-0.5">
                        <FormLabel className="font-medium">{line.title}</FormLabel>
                        <FormDescription>
                          {t('orders:returns.open.returnable', { count: max })}
                        </FormDescription>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={max}
                          className="w-20 text-right tabular-nums"
                          aria-label={t('orders:returns.open.quantityFor', { title: line.title })}
                          aria-invalid={fieldState.invalid}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </fieldset>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:returns.open.reason')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder={t('orders:returns.open.reasonPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:returns.open.note')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormDescription>{t('orders:returns.open.noteHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="resize"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="grid gap-1">
                    <FormLabel>{t('orders:returns.open.resize')}</FormLabel>
                    <FormDescription>{t('orders:returns.open.resizeHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="returnShippingPaidBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:returns.open.paidBy')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="default">
                        {t('orders:returns.open.paidByDefault')}
                      </SelectItem>
                      <SelectItem value="customer">
                        {t('orders:returns.open.paidByCustomer')}
                      </SelectItem>
                      <SelectItem value="store">{t('orders:returns.open.paidByStore')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="staffNote"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:returns.open.staffNote')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending
                  ? t('orders:returns.open.submitting')
                  : t('orders:returns.open.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
